import { useNavigate, useParams, useLocation } from 'react-router-dom';
import type { Round } from '@h2h/shared';
import { formatInviteCode } from '@h2h/shared';
import { AppShell, Content } from '../../components/ui/AppShell';
import { DesktopColumns } from '../../components/ui/DesktopColumns';
import { Avatar, MiniLeagueMark } from '../../components/ui/bits';
import { ChevronRight, Share2, Trophy, UserPlus } from '../../components/ui/icons';
import { useHaptics } from '../../hooks/useHaptics';
import { useAuth } from '../../context/AuthContext';
import {
  useCurrentRound,
  useLeagues,
  useMiniLeague,
  useMiniLeagueStandings,
  type MiniLeagueStandingRow
} from '../../hooks/useQueries';
import { isCyclingLeagueId, isLeagueActive, identityFor } from '../../data/leagues';
import { countdownTo, formatCount } from '../../lib/format';
import { shareMiniLeagueInvite, type MiniLeagueNavState } from './miniLeagueHelpers';
import { MiniLeagueExplainer } from './MiniLeagueExplainer';
import { LeagueNav } from './parts';
import { cn } from '../../lib/cn';
import { useOpenProfile } from '../../hooks/useOpenProfile';

export default function Standings() {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as MiniLeagueNavState | null;
  const { impact } = useHaptics();
  const { id } = useParams<{ id: string }>();
  const { uid } = useAuth();
  const { data: mini, isLoading } = useMiniLeague(id);
  const { data: rows } = useMiniLeagueStandings(uid, mini);
  const { data: leagues } = useLeagues();

  const baseLeague = leagues?.find((l) => l.leagueId === mini?.leagueId);
  const roundId = baseLeague?.currentRoundId ?? null;
  const { data: round } = useCurrentRound(mini?.leagueId, roundId);

  if (isLoading || !mini) {
    return (
      <AppShell>
        <Content>
          <LeagueNav label="Mini Leagues" onBack={() => navigate('/leagues')} />
          <div className="pt-20 text-center text-[14px] font-medium text-ink2">Loading…</div>
        </Content>
      </AppShell>
    );
  }

  const leagueName = baseLeague?.name ?? identityFor(mini.leagueId)?.name ?? 'League';
  const list = rows ?? [];
  const memberCount = mini.memberIds.length;
  const isCycling = isCyclingLeagueId(mini.leagueId);
  const playerCol = isCycling ? 'RIDER' : 'PLAYER';
  const roundCol = isCycling ? 'STAGE' : 'ROUND';

  const share = async () => {
    impact('light');
    await shareMiniLeagueInvite(mini);
  };

  return (
    <AppShell>
      <Content width="wide">
        <LeagueNav
          label="Mini leagues"
          onBack={() => navigate('/leagues', { state: navState })}
          right={
            <button
              type="button"
              aria-label="Manage league"
              onClick={() => {
                impact('light');
                navigate(`/leagues/${mini.miniLeagueId}/manage`);
              }}
              className="tap grid h-[38px] w-[38px] place-items-center rounded-full border border-black/[0.06] bg-card shadow-[0_2px_6px_-3px_rgba(0,0,0,0.15)]"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-ink">
                <circle cx="12" cy="12" r="1.6" />
                <circle cx="19" cy="12" r="1.6" />
                <circle cx="5" cy="12" r="1.6" />
              </svg>
            </button>
          }
        />

        <DesktopColumns
          railFirst
          asidePosition="left"
          asideWidth="340px"
          aside={
            <>
        {/* league header */}
        <div className="flex items-center gap-[13px] px-1 pt-3.5">
          <MiniLeagueMark icon={mini.icon} color={mini.color} size={48} />
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[22px] font-extrabold leading-[1.05] tracking-[-0.028em] text-ink">{mini.name}</h1>
            <div className="mt-[3px] truncate text-[12px] font-medium text-ink2">
              {memberCount} {memberCount === 1 ? 'friend' : 'friends'} · {leagueName} · Season
            </div>
          </div>
        </div>

        <div className="px-1 pt-3">
          <MiniLeagueExplainer leagueName={leagueName} variant="inline" />
        </div>

        {isLeagueActive(mini.leagueId) && round && (
          <CompactPicksReminder
            round={round}
            isCycling={isCycling}
            onPredict={() => {
              impact('medium');
              navigate(
                round.status === 'SETTLED'
                  ? `/play/${mini.leagueId}/results`
                  : `/play/${mini.leagueId}`
              );
            }}
          />
        )}
            </>
          }
          main={
            <>
        {/* standings table */}
        <div className="flex items-baseline justify-between px-1 pb-2 pt-[22px] md:pt-0">
          <span className="text-[15px] font-extrabold tracking-[-0.02em] text-ink">Standings</span>
          <span className="text-[12px] font-semibold text-ink2">
            {round?.status === 'SETTLED' ? `After ${round.label}` : 'This season'}
          </span>
        </div>

        {/* column header */}
        <div className="px-[6px]">
          <div className="flex items-center gap-[11px] px-2.5 pb-[9px]">
            <span className="w-[14px] text-center text-[10px] font-bold text-ink3">#</span>
            <span className="flex-1 text-[10px] font-bold tracking-[0.04em] text-ink3">{playerCol}</span>
            <span className="w-[50px] text-right text-[10px] font-bold text-ink3">{roundCol}</span>
            <span className="w-[46px] text-right text-[10px] font-bold text-ink3">PTS</span>
          </div>
        </div>

        <div className="px-1">
          <div className="overflow-hidden rounded-[20px] border border-black/[0.07] bg-card">
            {list.length === 0 ? (
              <div className="px-4 py-10 text-center text-[13px] font-medium text-ink2">No standings yet</div>
            ) : (
              list.map((row) => <TableRow key={row.userId} row={row} />)
            )}
          </div>
        </div>

        {/* invite row */}
        <div className="px-1 pt-3.5">
          <button
            type="button"
            onClick={share}
            className="tap flex w-full items-center gap-3 rounded-[18px] border border-accent/30 bg-accent-wash p-3 px-[15px] text-left"
          >
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-[11px] bg-accent">
              <UserPlus width={19} height={19} strokeWidth={2.2} className="text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-extrabold tracking-[-0.01em] text-accent-ink">Invite more mates</div>
              <div className="text-[12px] font-medium text-[#3E7A5E]">
                Code <span className="num font-bold">{formatInviteCode(mini.inviteCode)}</span>
              </div>
            </div>
            <Share2 width={20} height={20} strokeWidth={2.2} className="text-accent-ink" />
          </button>
        </div>
            </>
          }
        />
      </Content>
    </AppShell>
  );
}

function CompactPicksReminder({
  round,
  isCycling,
  onPredict
}: {
  round: Round;
  isCycling: boolean;
  onPredict: () => void;
}) {
  const settled = round.status === 'SETTLED';
  const open = round.status === 'OPEN' || round.status === 'LOCKING';
  const lockAt = open ? countdownTo(round.firstKickoffAt) : null;
  const picksWord = isCycling ? 'duels' : 'picks';
  const actionWord = settled ? 'Results' : isCycling ? 'Call' : 'Predict';

  return (
    <div className="px-1 pt-3.5">
      <button
        type="button"
        onClick={onPredict}
        className="tap flex w-full items-center gap-2.5 rounded-[14px] border border-accent/40 bg-card px-3 py-2.5 text-left"
      >
        <span className="h-2 w-2 shrink-0 rounded-full bg-accent shadow-[0_0_0_4px_rgba(36,197,106,0.16)]" />
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold tracking-[-0.01em] text-ink">
          {settled ? (
            <>{round.label} settled</>
          ) : open ? (
            <>
              {round.label} {picksWord} · locks <span className="font-extrabold">{lockAt}</span>
            </>
          ) : (
            <>{round.label} {picksWord} open soon</>
          )}
        </span>
        <span className="inline-flex shrink-0 items-center gap-[3px] text-[13px] font-extrabold tracking-[-0.01em] text-accent-ink">
          {actionWord}
          <ChevronRight width={14} height={14} strokeWidth={2.6} className="text-accent-ink" />
        </span>
      </button>
    </div>
  );
}

function TableRow({ row }: { row: MiniLeagueStandingRow }) {
  const you = row.isYou;
  const edge = !you && row.rank === 1 ? '#E3A82B' : !you && row.rank === 3 ? '#E1885B' : undefined;
  const openProfile = useOpenProfile();

  return (
    <button
      type="button"
      onClick={() => openProfile(row.userId, you)}
      className={cn(
        'tap flex w-full items-center gap-[11px] px-[14px] text-left',
        you ? 'bg-night py-[13px] pl-[13px]' : 'py-3 pl-[13px]',
        !you && row.rank === 1 && 'border-b border-black/[0.05]',
        !you && row.rank === 3 && 'border-t border-black/[0.05]'
      )}
      style={edge ? { borderLeft: `3px solid ${edge}` } : undefined}
    >
      <span
        className={cn(
          'num w-[14px] text-center text-[14px] font-extrabold tabular-nums',
          you ? 'text-accent' : 'text-ink'
        )}
      >
        {row.rank}
      </span>

      {you ? (
        <Avatar
          name={row.displayName}
          tone={row.avatarTone}
          size={34}
          className="shrink-0 ring-2 ring-accent"
        />
      ) : (
        <Avatar name={row.displayName} tone={row.avatarTone} size={34} />
      )}

      <div className="flex min-w-0 flex-1 items-center gap-[6px]">
        <span
          className={cn(
            'truncate text-[15px] font-bold tracking-[-0.01em]',
            you ? 'font-extrabold text-white' : 'text-ink'
          )}
        >
          {you ? 'You' : row.displayName}
        </span>
        {you && (
          <span className="rounded-[5px] bg-accent px-1.5 py-0.5 text-[9px] font-extrabold tracking-[0.03em] text-[#06371F]">
            YOU
          </span>
        )}
        {row.rank === 1 && !you && (
          <Trophy width={14} height={14} strokeWidth={1.4} className="shrink-0 text-gold-top" />
        )}
      </div>

      <span
        className={cn(
          'num w-[50px] text-right text-[12px] font-bold tabular-nums',
          you ? 'font-extrabold text-accent' : 'text-ink2'
        )}
      >
        +{formatCount(row.roundDelta)}
      </span>

      <span
        className={cn(
          'num w-[46px] text-right text-[15px] font-extrabold tabular-nums',
          you ? 'text-white' : 'text-ink'
        )}
      >
        {formatCount(row.seasonPoints)}
      </span>
    </button>
  );
}
